import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useStore } from 'Store';
import { getAssetTitle } from 'Utils';
import { getRecordingIdsString, getRecordingIdsStringByStorage } from 'Utils/itemUtils';
import { NUMBERING_EPISODE, TITLE } from '@24i/st-api-connector/utils';
import { getParentalRatingByModel, isAdult } from 'Utils/parentalControlUtils';
import { formatDay, getHoursAndMinutes, getDayAndMonth } from 'Utils/dateUtils';
import RecordingItem from '../../RecordingItem';
import RecordingGroup from '../../RecordingGroup';

const getSubtitle = (item) => {
    let genre = '';
    let ageRating = '';
    try {
        if (item.modelType === 'recording') {
            const channelName = item.getChannelName();
            const startTime = item.getStartTime();
            const endTime = item.getEndTime();

            const day = formatDay(new Date(startTime).getDay(), 2);
            return `${day}, ${getDayAndMonth(startTime)} | ${getHoursAndMinutes(
                startTime
            )} - ${getHoursAndMinutes(endTime)} | ${channelName}`;
        }
        const metaData = item.metadata ? item.metadata.metaData : item.metaData;
        genre = metaData.detail?.schedule?.program?.programCategory?.desc || '';
        ageRating = metaData.detail?.schedule?.program?.programValue?.parentalRating || '';
        if (ageRating) ageRating = `${ageRating.substr(1)}+`;
    } catch (err) {
        console.log(err);
    }
    return [genre, ageRating].filter((i) => i !== '' && !!i).join(' | ');
};

const getRecPlatformsFromUserData = (userData) => ({
    iptv:
        userData && userData.platforms && userData.platforms.length > 0
            ? userData.platforms.includes('IPTV')
            : false,
    go:
        userData && userData.platforms && userData.platforms.length > 0
            ? userData.platforms.includes('GO')
            : false
});

const ListItem = ({
    item,
    index,
    forceUpdate,
    isEditing,
    editedIds,
    planned,
    onSelect,
    setEditedIds,
    getRealId,
    onGroupCheckboxSelect
}) => {
    const { userData } = useStore();
    const recPlatforms = useRef(getRecPlatformsFromUserData(userData));

    const onItemCheckbox = useCallback(
        (checkedItem) => (toggle) => {
            const recordingIds = checkedItem.ids;
            const recordedOnBothPlatforms = Boolean(recordingIds.iptv && recordingIds.go);

            // TODO: Use the bellow commented code to get storage string in case we need it.
            // const storage = getPlatformsStorageString(recordingIds);
            // const storageString = recordedOnBothPlatforms
            // ? getPlatformsStorageString(recPlatforms.current) : storage;

            const idsString = recordedOnBothPlatforms
                ? getRecordingIdsStringByStorage(recordingIds, recPlatforms.current)
                : getRecordingIdsString(recordingIds);

            if (toggle) setEditedIds([...editedIds, idsString]);
            else {
                const delIndex = editedIds.indexOf(idsString);
                if (delIndex >= 0) {
                    const newItems = [...editedIds];
                    newItems.splice(delIndex, 1);
                    setEditedIds(newItems);
                }
            }
        },
        [editedIds]
    );

    const onGroupCheckbox = useCallback(
        (checkedItem) => (toggle) => {
            const { episodes } = checkedItem;
            const idsList = episodes.map((episode) => {
                const recordingIds = episode.ids;
                const recordedOnBothPlatforms = Boolean(recordingIds.iptv && recordingIds.go);

                return recordedOnBothPlatforms
                    ? getRecordingIdsStringByStorage(recordingIds, recPlatforms.current)
                    : getRecordingIdsString(recordingIds);
            });

            if (toggle) {
                const newIds = idsList.filter((idInGroup) => !editedIds.includes(idInGroup));
                setEditedIds([...editedIds, ...newIds]);
                onGroupCheckboxSelect([...editedIds, ...newIds], checkedItem?.data?.id);
            } else {
                const reducedIds = editedIds.filter((id) => !idsList.includes(id));
                setEditedIds(reducedIds);
                onGroupCheckboxSelect(reducedIds, checkedItem?.data?.id);
            }
        },
        [editedIds]
    );

    switch (item.modelType) {
        case 'recordingSeries':
            return (
                <RecordingGroup
                    key={item.getId()}
                    title={getAssetTitle(item, TITLE)}
                    subTitle={getSubtitle(item)}
                    iconUrl={item.getIconUrl()}
                    updateList={forceUpdate}
                    isFirst={index === 0}
                    isEditing={isEditing}
                    isSelected={item.episodes.every((ep) => editedIds.includes(getRealId(ep)))}
                    onCheckbox={onGroupCheckbox(item)}
                    parentalRating={getParentalRatingByModel(item)}
                    adult={isAdult(item)}
                    // TBD if this is needed TODO:
                    // IPTVonly={planned && Boolean(item.episodes?.every(ep => Boolean(ep.ids.iptv)))}
                >
                    {item.episodes
                        .filter((ep) => planned === ep.isPlanned())
                        .map((ep) => (
                            <RecordingItem
                                item={ep}
                                index={index}
                                key={getRealId(ep)}
                                onPress={() => onSelect(ep)}
                                title={getAssetTitle(ep, NUMBERING_EPISODE)}
                                subTitle={getSubtitle(ep)}
                                iconUrl={ep.getIconUrl()}
                                isEditing={isEditing}
                                isSelected={editedIds.includes(getRealId(ep))}
                                onCheckbox={onItemCheckbox(ep)}
                                IPTVonly={planned && Boolean(ep.ids.iptv)}
                                parentalRating={getParentalRatingByModel(ep)}
                                adult={isAdult(ep)}
                            />
                        ))}
                </RecordingGroup>
            );
        case 'recording':
            return (
                <RecordingItem
                    item={item}
                    index={index}
                    key={getRealId(item)}
                    onPress={() => onSelect(item)}
                    title={getAssetTitle(item)}
                    subTitle={getSubtitle(item)}
                    iconUrl={item.getIconUrl()}
                    isFirst={index === 0}
                    isEditing={isEditing}
                    isSelected={editedIds.includes(getRealId(item))}
                    onCheckbox={onItemCheckbox(item)}
                    IPTVonly={planned && Boolean(item.ids.iptv)}
                    parentalRating={getParentalRatingByModel(item)}
                    adult={isAdult(item)}
                />
            );
        default:
            return null;
    }
};

ListItem.propTypes = {
    item: PropTypes.shape({}).isRequired,
    index: PropTypes.number.isRequired,
    planned: PropTypes.bool.isRequired,
    editedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    isEditing: PropTypes.bool.isRequired,
    onSelect: PropTypes.func.isRequired,
    setEditedIds: PropTypes.func.isRequired,
    forceUpdate: PropTypes.func.isRequired,
    getRealId: PropTypes.func.isRequired,
    onGroupCheckboxSelect: PropTypes.func
};

ListItem.defaultProps = {
    onGroupCheckboxSelect: () => {}
};

export default ListItem;
